import React, { useState, useEffect } from 'react';
import { SyncMapItem } from '../types';
import { Play, Mic, Check, X, ShieldAlert, Lock, Pause, Volume2, Sparkles, Activity, Wallet, Loader2, Users, Trophy, Crosshair, ArrowLeftRight, AudioWaveform } from 'lucide-react';
import { validateZuluConcordance } from '../utils/axiomGate';
import { annotations } from '../lib/api';
import { useAuth } from '../lib/authContext';

interface HealerInterfaceProps {
  data: SyncMapItem[];
  onComplete: () => void;
  onTransaction: (result: any) => void; 
}

const HealerInterface: React.FC<HealerInterfaceProps> = ({ data, onComplete, onTransaction }) => {
  const [queue, setQueue] = useState(data.filter(item => item.status === 'drafted'));
  const { user } = useAuth();

  const handleSeal = async (itemId: string, finaltext: string) => {
     setQueue(prev => prev.filter(i => i.id !== itemId));
     try {
        const result = await annotations.submit({ frameId: itemId, transcription: finaltext, userId: user?.id });
        onTransaction(result);
     } catch (e) {
        console.error("Backend Error", e);
     }
  };

  if (queue.length === 0) {
    return (
      <div className="text-center py-20 animate-in fade-in zoom-in-95 duration-500">
        <div className="w-16 h-16 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-6 border border-zinc-800">
            <Check className="w-8 h-8 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-white">Queue Cleared</h2>
        <p className="text-zinc-500 mt-2 max-w-md mx-auto">All low-confidence segments have been healed and vaulted.</p>
        <button onClick={onComplete} className="mt-8 px-8 py-3 bg-white text-black font-semibold rounded-full hover:bg-zinc-200 transition-colors">
            Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto pb-20">
      <div className="flex items-center justify-between mb-8 sticky top-0 bg-[#09090b]/95 backdrop-blur-sm z-40 py-4 border-b border-zinc-900">
        <div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Healer Console</h2>
          <div className="flex items-center gap-2 mt-1">
             <span className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
               {queue.length} Tasks Left
             </span>
             <span className="text-zinc-600">•</span>
             <span className="flex items-center gap-1 text-xs text-purple-400 font-bold uppercase">
                <Users className="w-3 h-3" /> {user?.clan || "Ronin"}
             </span>
          </div>
        </div>
        <div className="flex gap-3">
           <div className="px-3 py-1 bg-red-500/10 text-red-400 rounded-full text-[10px] font-bold uppercase tracking-widest border border-red-500/20 flex items-center gap-2">
            <Lock className="w-3 h-3" />
            Axiom Gate
          </div>
        </div>
      </div>

      <div className="space-y-12">
        {queue.map((item) => (
          <HealerItem key={item.id} item={item} onSeal={handleSeal} />
        ))}
      </div>
    </div>
  );
};

const HealerItem: React.FC<{ item: SyncMapItem, onSeal: (id: string, text: string) => void }> = ({ item, onSeal }) => {
  const [transcription, setTranscription] = useState(item.draft?.transcription || "");
  const [axiomErrors, setAxiomErrors] = useState<string[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visualLinkMode, setVisualLinkMode] = useState(false);

  useEffect(() => {
    // Only validate if Zulu/Nguni for now (demo logic)
    if (item.language?.includes("Zulu")) {
        const result = validateZuluConcordance(transcription);
        setAxiomErrors(result.violations);
    }
  }, [transcription, item.language]);

  const handlePlay = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) setTimeout(() => setIsPlaying(false), 3000);
  };

  const handleTTSPlay = () => {
    setIsTTSPlaying(true);
    // Simulate TTS playback
    setTimeout(() => setIsTTSPlaying(false), 2000);
  };

  const handleSuggestionClick = (word: string) => {
    const newText = transcription.trim() ? `${transcription.trim()} ${word}` : word;
    setTranscription(newText);
    setActiveSuggestion(word);
    setTimeout(() => setActiveSuggestion(null), 200);
  };

  const draftWords = item.draft?.transcription?.split(/\s+/) || [];
  
  return (
    <div className="group relative bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden hover:border-zinc-700 transition-all duration-300">
      
      {/* Media Section */}
      <div className="relative h-64 w-full bg-black">
         <img 
            src={item.imageUrl} 
            alt="Context" 
            className={`w-full h-full object-cover transition-all duration-500 ${visualLinkMode ? 'opacity-40 scale-105' : 'opacity-80 group-hover:opacity-100'}`} 
         />
         
         {/* Visual Linking Overlay (Simulated) */}
         {visualLinkMode && (
             <div className="absolute inset-0 flex items-center justify-center animate-in fade-in duration-300">
                 <div className="border-2 border-dashed border-green-500 bg-green-500/10 w-32 h-32 rounded-lg flex items-center justify-center relative">
                    <span className="bg-green-600 text-white text-[10px] px-1 absolute -top-3 left-2">Object: PERSON</span>
                    <Crosshair className="text-green-500 w-8 h-8 opacity-50" />
                 </div>
             </div>
         )}

         <div className="absolute top-4 left-4 flex gap-2">
            <div className="bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10 text-xs text-white font-mono">
                {item.language?.toUpperCase()} • {item.timestamp.toFixed(2)}s
            </div>
            {item.draft?.isCodeSwitched && (
                <div className="bg-orange-500/80 backdrop-blur-md px-3 py-1.5 rounded-lg text-xs text-white font-bold flex items-center gap-1 border border-orange-400/50">
                    <ArrowLeftRight className="w-3 h-3" /> Code-Switched
                </div>
            )}
         </div>

         {!visualLinkMode && (
             <div className="absolute inset-0 flex items-center justify-center">
                 <button 
                    onClick={handlePlay}
                    className={`
                        w-16 h-16 rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 transition-all duration-300 group/play
                        ${isPlaying ? 'bg-purple-600/90 text-white scale-110' : 'bg-black/40 text-white hover:bg-black/60'}
                    `}
                 >
                    {isPlaying ? <Activity className="w-6 h-6 animate-pulse" /> : <Play className="w-6 h-6 ml-1" />}
                 </button>
             </div>
         )}
      </div>

      {/* Controls */}
      <div className="p-6 md:p-8 space-y-6">
        
        {/* Axiom Errors */}
        {axiomErrors.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 p-4 rounded-xl flex items-start gap-4">
              <Lock className="w-5 h-5 text-red-500 mt-1" />
              <div>
                <h4 className="text-red-400 text-sm font-bold uppercase">Linguistic Axiom Violation</h4>
                {axiomErrors.map((err, idx) => <p key={idx} className="text-red-300/80 text-sm font-mono">{err}</p>)}
              </div>
            </div>
        )}

        <div className="grid grid-cols-1 gap-6">
            <div className="space-y-3">
                <div className="flex justify-between items-center">
                     <label className="text-xs text-zinc-500 font-bold uppercase tracking-widest flex items-center gap-2">
                        <Volume2 className="w-3 h-3" /> Transcript Healer
                    </label>
                    
                    <div className="flex items-center gap-2">
                         {/* FEATURE: TTS VALIDATION */}
                        <button 
                            onClick={handleTTSPlay}
                            className={`text-[10px] px-2 py-0.5 rounded border flex items-center gap-1 transition-colors
                                ${isTTSPlaying ? 'bg-purple-500/20 text-purple-400 border-purple-500' : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-white'}
                            `}
                        >
                            <AudioWaveform className={`w-3 h-3 ${isTTSPlaying ? 'animate-pulse' : ''}`} />
                            {isTTSPlaying ? 'VALIDATING AUDIO...' : 'VERIFY SYNTHETIC VOICE'}
                        </button>

                        <button 
                            onClick={() => setVisualLinkMode(!visualLinkMode)}
                            className={`text-[10px] px-2 py-0.5 rounded border flex items-center gap-1 transition-colors
                                ${visualLinkMode ? 'bg-green-500/10 border-green-500 text-green-400' : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-white'}
                            `}
                        >
                            <Crosshair className="w-3 h-3" />
                            {visualLinkMode ? 'LINKING ACTIVE' : 'LINK VISUALS'}
                        </button>
                    </div>
                </div>
               
                <div className={`relative group/input ${axiomErrors.length > 0 ? 'ring-1 ring-red-900/50 rounded-xl' : ''}`}>
                    <textarea 
                        className={`w-full bg-black/40 border rounded-xl p-4 text-lg font-medium text-white focus:outline-none min-h-[120px] resize-none
                        ${axiomErrors.length > 0 ? 'border-red-900/50' : 'border-zinc-800 focus:border-purple-500/50'}
                        `}
                        value={transcription}
                        onChange={(e) => setTranscription(e.target.value)}
                        placeholder={`Type ${item.language} transcription here...`}
                    />
                    <div className="absolute bottom-3 right-3 flex gap-2">
                         <button className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white">
                            <Mic className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Suggestions */}
                <div className="flex flex-wrap gap-2 pt-1">
                    {draftWords.map((word, i) => (
                        <button 
                            key={i}
                            onClick={() => handleSuggestionClick(word)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-purple-500 hover:text-white transition-all`}
                        >
                            {word}
                        </button>
                    ))}
                </div>
            </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-6 border-t border-zinc-800/50">
           <button className="text-xs text-zinc-500 hover:text-red-400 transition-colors flex items-center gap-2 font-medium">
             <X className="w-4 h-4" /> Discard
           </button>
           
           <button 
            onClick={() => { setIsSubmitting(true); onSeal(item.id, transcription); setIsSubmitting(false); }}
            disabled={axiomErrors.length > 0 || isSubmitting}
            className={`px-8 py-3 rounded-xl text-sm font-bold tracking-wide transition-all flex items-center gap-3 shadow-xl
              ${axiomErrors.length > 0 ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed' : 'bg-white text-black hover:bg-zinc-200'}
            `}
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : axiomErrors.length > 0 ? <Lock className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            <span>{axiomErrors.length > 0 ? 'LOCKED' : 'SEAL & EARN'}</span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default HealerInterface;